import { ChangeDetectionStrategy, Component } from '@angular/core';
import { RappiderCrudFieldsDisplayModule, RappiderTitleToolbarModule } from '@rappider/rappider-components';
import { HeadingComponentConfig, HeadingType } from '@rappider/rappider-components/utils';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { ActivatedRoute } from '@angular/router';
import { selectActiveProject } from 'libs/project/src/lib/states/active-project-state/active-project.selectors';
import { GetAiFunctionCalls, selectAiFunctionCallLoaded, selectAiFunctionCalls } from '@rappider/shared/data-stores';
import { catchError, map, mergeMap, of, Subscription } from 'rxjs';
import { AiFunctionCall, Project } from '@rappider/rappider-sdk';
import * as lodash from 'lodash';

import { AiFunctionCallControllerService } from '@rappider/rappider-sdk';

@Component({
  selector: 'rappider-ai-function-execute',
  standalone: true,
  imports: [
    RappiderCrudFieldsDisplayModule,
    NzInputModule,
    NzButtonModule,
    RappiderTitleToolbarModule,
    CommonModule,
    FormsModule,
  ],
  templateUrl: './ai-function-execute.component.html',
  styleUrls: ['./ai-function-execute.component.scss'],
})
export class RappiderAiFunctionExecuteComponent {
  titleConfig: HeadingComponentConfig = { content: 'Test AI Function', type: HeadingType.H3 };

  breadcrumbs: { label: string; redirectUrl?: string }[] = [
    {
      label: 'AI Functions',
      redirectUrl: '/ai/ai-function'
    },
    {
      label: 'This function',
    },
  ];

  apiCallTestInput = '';

  isAITestInProgress = false;

  aiFunctions: AiFunctionCall[] | null = null;
  /* active project to get the related data */
  activeProject: Project | undefined;
  /* data loading flag */
  isDataLoaded = false;
  activeItemId: string | undefined;
  activeItem?: AiFunctionCall | undefined;

  /* subscriptions for the data */
  subscriptions: Subscription[] = [];

  constructor(
    private store: Store<any>,
    private route: ActivatedRoute,
    private aiFunctionCallControllerService: AiFunctionCallControllerService,
  ) { }

  ngOnInit(): void {
    this.getPathParameters();
    this.subscribeToData();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  /* get path parameters for view mode and editing item id */
  getPathParameters() {
    this.activeItemId = this.route.snapshot?.params?.itemId;
  }

  onExecuteApiCall() {
    this.isAITestInProgress = true;
    setTimeout(() => {
      this.isAITestInProgress = false;
    }, 2000);
  }

  /* get data for crud list */
  subscribeToData() {
    this.subscriptions = [
      // get active project
      this.store.select(selectActiveProject).subscribe(activeProject => {
        this.activeProject = activeProject;
      }),
      // get if entities loaded to the store
      this.store.select(selectAiFunctionCallLoaded).subscribe(isDataLoaded => {
        this.isDataLoaded = !!isDataLoaded;
        if (!isDataLoaded) {
          this.store.dispatch(GetAiFunctionCalls());
        }
      }),
      // get entities data
      this.store.select(selectAiFunctionCalls).subscribe(aiFunctions => {
        this.aiFunctions = aiFunctions;

        // set active item if view mode is edit or update
        if (this.activeItemId) {
          this.activeItem = lodash.find(aiFunctions, { id: this.activeItemId });

          if (this.activeItem) {
            // update breadcrumbs 2nd item label
            this.breadcrumbs[1].label = this.activeItem.title;
            this.breadcrumbs[1].redirectUrl = `/ai/ai-function/${this.activeItemId}`;
            // update title
            this.titleConfig.content = this.activeItem.title;
            this.titleConfig.type = HeadingType.H3;
          }
        }
      }
      ),
    ];
  }

  updateFunctionAndExecute(): void {
    if (this.activeItemId && this.activeItem?.id && this.apiCallTestInput) {
      const params = {
        id: this.activeItemId,
        body: {
          ...this.activeItem,
          functionPrompt: this.apiCallTestInput
        }
      };
      this.isAITestInProgress = true;

      this.aiFunctionCallControllerService.updateById(params).pipe(
        mergeMap((aiFunctionCall) => {
          console.log('AI Function Call updated:', aiFunctionCall);
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          return this.aiFunctionCallControllerService.executeFunctionToolsCall$Json({ id: this.activeItemId! }).pipe(
            map((response) => {
              this.isAITestInProgress = false;
              console.log('AI Function executed successfully:', response);
              return response;
            }),
            catchError((error) => {
              this.isAITestInProgress = false;
              console.error('Error executing function call:', error);
              return of(null);
            })
          );
        }),
        catchError((error) => {
          this.isAITestInProgress = false;
          console.error('Error updating function call:', error);
          return of(null);
        })
      ).subscribe({
        next: (response) => {
          console.log('Final response:', response);
          this.isAITestInProgress = false;
        },
        error: (error) => {
          console.error('Error in updateFunctionAndExecute:', error);
          this.isAITestInProgress = false;
        },
        complete: () => {
          console.log('updateFunctionAndExecute completed');
          this.isAITestInProgress = false;
        }
      });
    }
  }
}
